import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Star, Zap, Heart, Brain, Users, Palette, Target, Shield, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { completeMission } from '../../utils/missionHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Superpower categories for middle schoolers
const superpowers = [
  {
    id: 'creative',
    name: 'Super Creative',
    icon: Palette,
    color: 'from-purple-500 to-pink-500',
    description: 'You have amazing ideas and love making things!',
    examples: ['Coming up with stories', 'Drawing & art', 'Solving problems in new ways']
  },
  {
    id: 'smart',
    name: 'Super Smart',
    icon: Brain,
    color: 'from-blue-500 to-cyan-500',
    description: 'You love learning and figuring things out!',
    examples: ['Good at school', 'Asking great questions', 'Remembering facts']
  },
  {
    id: 'friendly',
    name: 'Super Friendly',
    icon: Users,
    color: 'from-green-500 to-emerald-500',
    description: 'Everyone loves being around you!',
    examples: ['Making friends easily', 'Including others', 'Great listener']
  },
  {
    id: 'brave',
    name: 'Super Brave',
    icon: Shield,
    color: 'from-orange-500 to-red-500',
    description: "You're not afraid to try new things!",
    examples: ['Speaking up in class', 'Standing up for others', 'Trying new activities']
  },
  {
    id: 'helpful',
    name: 'Super Helpful',
    icon: Heart,
    color: 'from-pink-500 to-rose-500',
    description: 'You always want to help others!',
    examples: ['Helping classmates', 'Volunteering', 'Being kind']
  },
  {
    id: 'focused',
    name: 'Super Focused',
    icon: Target,
    color: 'from-amber-500 to-yellow-500',
    description: 'When you set a goal, you achieve it!',
    examples: ['Finishing projects', 'Working hard', 'Not giving up']
  },
  {
    id: 'energetic',
    name: 'Super Energetic',
    icon: Zap,
    color: 'from-yellow-400 to-orange-500',
    description: "You've got energy and enthusiasm!",
    examples: ['Sports & activities', 'Getting others excited', 'Always ready to go']
  },
  {
    id: 'leader',
    name: 'Natural Leader',
    icon: Star,
    color: 'from-indigo-500 to-purple-500',
    description: 'People look up to you and follow your lead!',
    examples: ['Group projects', 'Team captain', 'Making decisions']
  }
];

export default function MSStrengthsPage() {
  const { user, token } = useAuth();
  const [selectedPowers, setSelectedPowers] = useState([]);
  const [saving, setSaving] = useState(false);
  const missionTriggered = useRef(false);

  useEffect(() => {
    if (user?.strengths) {
      setSelectedPowers(user.strengths);
    }
  }, [user]);

  const togglePower = async (powerId) => {
    const newPowers = selectedPowers.includes(powerId)
      ? selectedPowers.filter(p => p !== powerId)
      : [...selectedPowers, powerId].slice(0, 3);
    
    setSelectedPowers(newPowers);
    
    // Complete mission when 3 powers are selected
    if (newPowers.length === 3 && !missionTriggered.current && token) {
      missionTriggered.current = true;
      completeMission(token, 'strength');
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, {
        strengths: newPowers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 text-center"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">My Superpowers</h1>
        <p className="text-slate-300">Pick up to 3 superpowers that describe YOU!</p>
        <p className="text-slate-300 text-sm mt-1">
          {selectedPowers.length}/3 selected
        </p>
      </motion.div>

      {/* Superpowers Grid */}
      <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        {superpowers.map((power, index) => {
          const Icon = power.icon;
          const isSelected = selectedPowers.includes(power.id);
          
          return (
            <motion.button
              key={power.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => togglePower(power.id)}
              disabled={!isSelected && selectedPowers.length >= 3}
              className={`p-6 rounded-2xl text-left transition-all ${
                isSelected
                  ? `bg-gradient-to-br ${power.color} shadow-lg scale-[1.02]`
                  : selectedPowers.length >= 3
                  ? 'bg-slate-800/50 opacity-50 cursor-not-allowed'
                  : 'bg-slate-800 hover:bg-slate-700'
              }`}
              data-testid={`power-${power.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isSelected ? 'bg-white/20' : `bg-gradient-to-br ${power.color}`
                }`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{power.name}</h3>
                    {isSelected && (
                      <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white">
                        Selected!
                      </span>
                    )}
                  </div>
                  <p className={`text-sm mt-1 ${isSelected ? 'text-white/80' : 'text-slate-300'}`}>
                    {power.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {power.examples.map((ex, i) => (
                      <span
                        key={i}
                        className={`text-xs px-2 py-1 rounded-full ${
                          isSelected ? 'bg-white/20 text-white' : 'bg-slate-700 text-slate-300'
                        }`}
                      >
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Selected Summary */}
      {selectedPowers.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 max-w-2xl mx-auto text-center"
        >
          <h3 className="text-white font-semibold mb-2">Your Superpowers:</h3>
          <div className="flex justify-center gap-2 flex-wrap">
            {selectedPowers.map(powerId => {
              const power = superpowers.find(p => p.id === powerId);
              if (!power) return null;
              return (
                <span
                  key={powerId}
                  className={`px-4 py-2 rounded-full bg-gradient-to-r ${power.color} text-white font-medium`}
                >
                  {power.name}
                </span>
              );
            })}
          </div>
          {saving && (
            <p className="text-slate-300 text-sm mt-2">Saving...</p>
          )}
        </motion.div>
      )}
    </div>
  );
}
